import {isLoggedIn} from '@github-ui/client-env'
import {IssueMarkdownViewer} from '@github-ui/commenting/IssueMarkdownViewer'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {ReactionViewerGroupsFragment} from '@github-ui/reaction-viewer/ReactionViewer'
import {ReactionViewerAnchor} from '@github-ui/reaction-viewer/ReactionViewerAnchor'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {IssueViewerSecondaryViewServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'
import {AddSubIssueButtonGroup} from '@github-ui/sub-issues/AddSubIssueButtonGroup'
import {useSubIssuesPresence} from '@github-ui/sub-issues/SubIssuesPresenceContext'
import {useCanEditSubIssues} from '@github-ui/sub-issues/useCanEditSubIssues'
import {useHasSubIssues} from '@github-ui/sub-issues/useHasSubIssues'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import type {TaskItem} from '@github-ui/use-tasklist'
import {useSafeTimeout} from '@primer/react'
import {clsx} from 'clsx'
import React, {Suspense, useCallback, useMemo} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {ERRORS} from '../constants/errors'
import {useCreateIssueFromChecklistItemMutation} from '../mutations/create-issue-from-checklist-item-mutation'
import {useCreateSubIssueFromChecklistItemMutation} from '../mutations/create-sub-issue-from-checklist-item-mutation'
import {useUpdateIssueBodyMutation} from '../mutations/use-update-issue-body-mutation'
import type {IssueBodyHeaderActionsNode} from './issue-body-header-actions-query-types'
import styles from './IssueBodyViewer.module.css'
import {PinnedCommentDisplay, type PinnedCommentDisplayFragment} from './PinnedCommentDisplay'

const ReactionViewer = React.lazy(() => import('@github-ui/reaction-viewer/ReactionViewer'))

export type IssueBodyViewerFragment = Pick<IssueBodyHeaderActionsNode, 'id'>

// RELAY-CLEANUP(IssueBody): widens nullable/undefined fields so the still-Relay parent data is
// assignable. Remove once IssueBody leaves Relay.
export type IssueBodyViewerSubIssuesFragment = RelayCompat<
  Pick<
    IssueBodyHeaderActionsNode,
    'id' | 'databaseId' | 'number' | 'state' | 'stateReason' | 'title' | 'titleHTML' | 'url' | 'viewerCanUpdateMetadata'
  > & {
    readonly repository: Pick<
      IssueBodyHeaderActionsNode['repository'],
      'isArchived' | 'name' | 'nameWithOwner' | 'visibility' | 'viewerCanPush'
    > & {
      readonly owner: Pick<IssueBodyHeaderActionsNode['repository']['owner'], 'login'>
    }
    readonly subIssuesSummary: Pick<IssueBodyHeaderActionsNode['subIssuesSummary'], 'completed' | 'total'>
  }
>

// RELAY-CLEANUP(IssueViewerSecondaryViewQuery): widens nullable fields so the still-Relay parent
// data is assignable. Remove when IssueViewerSecondaryViewQuery leaves Relay.
export type IssueBodyViewerPinnedCommentSecondaryFragment = RelayCompat<
  Pick<NonNullable<NonNullable<IssueViewerSecondaryViewServerQueryQuery['repository']>['issue']>, 'pinnedIssueComment'>
>

export type IssueBodyViewerProps = {
  owner: string
  repo: string
  issueNumber: number
  html: SafeHTMLString
  markdown: string
  comment: IssueBodyViewerFragment
  onLinkClick?: (event: MouseEvent) => void
  issueBodyRef?: React.RefObject<HTMLDivElement | null>
  bodyVersion: string
  locked: boolean
  reactable: ReactionViewerGroupsFragment
  subIssues?: IssueBodyViewerSubIssuesFragment | null
  pinnedComment?: PinnedCommentDisplayFragment | null
  pinnedCommentSecondary?: IssueBodyViewerPinnedCommentSecondaryFragment | null
  insideSidePanel?: boolean
  repositoryId?: string
  onIssueEditStateChange?: (edited: boolean) => void
  isReadonly: boolean
  viewerCanCreateIssues: boolean
  viewerCanUpdate: boolean
  issueBodyTaskListAction?: React.ReactNode
}

export function IssueBodyViewer({
  owner,
  repo,
  issueNumber,
  html,
  markdown,
  comment,
  onLinkClick,
  issueBodyRef,
  bodyVersion,
  locked,
  reactable,
  subIssues: dataSubIssues = null,
  pinnedComment = null,
  pinnedCommentSecondary = null,
  insideSidePanel,
  repositoryId,
  onIssueEditStateChange,
  isReadonly,
  viewerCanCreateIssues,
  viewerCanUpdate,
  issueBodyTaskListAction,
}: IssueBodyViewerProps) {
  const environment = useRelayEnvironment()
  const commitUpdateIssueBody = useUpdateIssueBodyMutation({environment, owner, repo, issueNumber})
  const createIssueMutation = useCreateIssueFromChecklistItemMutation(environment)
  const createSubIssueMutation = useCreateSubIssueFromChecklistItemMutation(environment, comment.id)
  const {safeSetTimeout, safeClearTimeout} = useSafeTimeout()
  const primaryHasSubIssues = useHasSubIssues(dataSubIssues)
  // Read the section's overlaid presence (via context) so this empty-state button and the
  // section stay complementary (exactly one shows). Falls back to the Relay summary otherwise.
  const overlaidHasSubIssues = useSubIssuesPresence()
  const hasSubIssues = overlaidHasSubIssues ?? primaryHasSubIssues
  const canEditSubIssues = useCanEditSubIssues(dataSubIssues)
  const showAddSubIssueButton = canEditSubIssues && dataSubIssues && !hasSubIssues && !isReadonly
  const showIssueBodyTaskListActions = Boolean(showAddSubIssueButton || issueBodyTaskListAction)

  const onSave = useCallback(
    (newBody: string, onCompleted: () => void, onError: () => void) => {
      commitUpdateIssueBody({
        input: {issueId: comment.id, body: newBody, bodyVersion},
        onCompleted,
        onError,
      })
    },
    [bodyVersion, comment.id, commitUpdateIssueBody],
  )

  const {addToast} = useToastContext()

  /**
   * * Handles conversion completion timing - can't immediately set isConverting to false
   * because UI updates (rendering converted items as links) happen asynchronously.
   * Uses MutationObserver to wait for DOM changes ensuring loading state persists
   * until visual conversion is complete.
   **/
  const handleConversionComplete = useCallback(
    (
      id: string,
      setIsConverting: (isConverting: boolean) => void,
      onCompletedCallback?: () => void,
      convertingToSubIssue = false,
    ) => {
      let timeoutId: ReturnType<typeof safeSetTimeout> | null = null

      const completeMutation = () => {
        setIsConverting(false)
        onCompletedCallback?.()

        if (timeoutId) {
          safeClearTimeout(timeoutId)
        }
      }

      if (convertingToSubIssue) {
        // No need for observation as the rerender is triggered immediately
        requestAnimationFrame(() => {
          completeMutation()
        })
        return
      }

      const convertedItem = document.querySelector(`#checkbox-item-${id}`)
      if (convertedItem?.querySelector('a')) {
        completeMutation()
        return
      }

      const observer = new MutationObserver(mutationsList => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const safeHTMLBox = document.querySelector(`#checkbox-item-${id}`)

            if (safeHTMLBox?.querySelector('a')) {
              observer.disconnect()
              completeMutation()
              return
            }
          }
        }
      })

      // Observing the closest element that doesn't rerender on mutation complete
      const issueContainer = document.getElementById('issue-body-viewer')
      if (issueContainer) {
        observer.observe(issueContainer, {childList: true, subtree: true})
      } else {
        completeMutation()
        return
      }

      // If we don't find the newly inserted link, disconnect
      timeoutId = safeSetTimeout(() => {
        observer.disconnect()
        completeMutation()
      }, 3000)
    },
    [safeSetTimeout, safeClearTimeout],
  )

  const onConvertToIssue = useCallback(
    (
      task: TaskItem,
      setIsConverting: (converting: boolean) => void,
      onCompletedCallback?: () => void,
      onErrorCallback?: (error: Error) => void,
    ) => {
      if (!(repositoryId && onIssueEditStateChange && task.position)) return
      if (!dataSubIssues) return
      const position = task.position

      setIsConverting(true)
      const onSuccess = () => {
        onIssueEditStateChange(true)
        handleConversionComplete(task.id, setIsConverting, onCompletedCallback)
      }
      const onError = (error: Error) => {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({type: 'error', message: ERRORS.couldNotConvertIssue})
        setIsConverting(false)
        onErrorCallback?.(error)
      }

      // Each mutateAsync call resolves its own promise, so concurrent conversions settle
      // independently. Per-call mutate callbacks would be clobbered by the hook's shared
      // MutationObserver, leaving earlier rows stuck spinning.
      const convert = async () => {
        try {
          await createIssueMutation.mutateAsync({
            owner: dataSubIssues.repository.owner.login,
            repository: dataSubIssues.repository.name,
            issueNumber: dataSubIssues.number,
            title: task.title,
            position,
          })
        } catch (error) {
          onError(error as Error)
          return
        }
        onSuccess()
      }
      void convert()
    },
    [repositoryId, onIssueEditStateChange, dataSubIssues, createIssueMutation, addToast, handleConversionComplete],
  )

  const onConvertToSubIssue = useMemo(
    () =>
      (
        task: TaskItem,
        setIsConverting: (converting: boolean) => void,
        onCompletedCallback?: () => void,
        onErrorCallback?: (error: Error) => void,
      ) => {
        if (!(repositoryId && onIssueEditStateChange && task.position)) return
        if (!dataSubIssues) return
        const position = task.position

        setIsConverting(true)
        const onSuccess = () => {
          onIssueEditStateChange(true)
          handleConversionComplete(task.id, setIsConverting, onCompletedCallback, true)
        }
        const onError = (error: Error) => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({type: 'error', message: ERRORS.couldNotConvertIssue})
          setIsConverting(false)
          onErrorCallback?.(error)
        }

        // Each mutateAsync call resolves its own promise, so concurrent conversions settle
        // independently. Per-call mutate callbacks would be clobbered by the hook's shared
        // MutationObserver, leaving earlier rows stuck spinning.
        const convert = async () => {
          try {
            await createSubIssueMutation.mutateAsync({
              owner: dataSubIssues.repository.owner.login,
              repository: dataSubIssues.repository.name,
              issueNumber: dataSubIssues.number,
              body: markdown,
              position,
              title: task.title,
            })
          } catch (error) {
            onError(error as Error)
            return
          }
          onSuccess()
        }
        void convert()
      },
    [
      repositoryId,
      onIssueEditStateChange,
      dataSubIssues,
      createSubIssueMutation,
      addToast,
      markdown,
      handleConversionComplete,
    ],
  )

  const modernUx = isFeatureEnabled('issues_modern_ux')

  return (
    <div
      className={clsx(styles.IssueBody, modernUx && styles.IssueBodyModernUx)}
      ref={issueBodyRef}
      id="issue-body-viewer"
      data-testid="issue-body-viewer"
    >
      <IssueMarkdownViewer
        html={html}
        markdown={markdown}
        onSave={onSave}
        onLinkClick={onLinkClick}
        onConvertToIssue={onConvertToIssue}
        onConvertToSubIssue={onConvertToSubIssue}
        viewerCanCreateIssues={viewerCanCreateIssues}
        viewerCanUpdate={viewerCanUpdate}
      />
      <div className={styles.IssueBodyTaskList}>
        {showIssueBodyTaskListActions && (
          <div className={styles.IssueBodySubIssueButtonContainer}>
            {showAddSubIssueButton ? (
              <AddSubIssueButtonGroup
                issue={dataSubIssues}
                insideSidePanel={insideSidePanel}
                trailingAction={issueBodyTaskListAction}
              />
            ) : (
              issueBodyTaskListAction
            )}
          </div>
        )}
        {!isReadonly && (
          <Suspense fallback={<ReactionViewerAnchor />}>
            <ReactionViewer
              subjectId={comment.id}
              canReact={isLoggedIn() && !locked}
              reactionGroups={reactable}
              showReactors={isFeatureEnabled('see_who_reacted')}
            />
          </Suspense>
        )}
      </div>
      {pinnedComment && <PinnedCommentDisplay issue={pinnedComment} secondary={pinnedCommentSecondary} />}
    </div>
  )
}
